import { type Page_SlugS_productizedService$key } from '#app/__generated__/relay/Page_SlugS_productizedService.graphql.js';
import { type Page_SlugSQuery } from '#app/__generated__/relay/Page_SlugSQuery.graphql.js';
import { Grid } from '#app/components/Grid/Grid.js';
import { GridColumn } from '#app/components/Grid/GridColumn.js';
import { NotFound } from '#app/components/NotFound/NotFound.js';
import { ProductizedServiceAdminActionsButton } from '#app/components/ProductizedServiceAdminActions/ProductizedServiceAdminActions.js';
import { ProductizedServicePreviewBanner } from '#app/components/ProductizedServicePreviewBanner/ProductizedServicePreviewBanner.js';
import { RequestRecommendationCTA } from '#app/components/RequestRecommendation/RequestRecommendationCTA/RequestRecommendationCTA.js';
import { SSRSafeRender } from '#app/components/SSRSafeRender/SSRSafeRender.js';
import { useUserAvailabilityModal } from '#app/components/UserAvailabilityModal/hooks/useUserAvailabilityModal.js';
import { ProductizedService } from '#app/features/productizedService/components/ProductizedServiceLayout/ProductizedService.js';
import { ProductizedServiceSidebar } from '#app/features/productizedService/components/ProductizedServiceLayout/ProductizedServiceSidebar.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useHasCompletedProfile } from '#app/hooks/useHasCompletedProfile.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useRegisterAnalyticEvent } from '#app/hooks/useRegisterAnalyticEvent.js';
import { useUpgradeToPro } from '#app/hooks/useUpgradeToPro.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { MainLayout } from '#app/layouts/MainLayout/MainLayout.js';
import { type PageProps } from '#app/types/router.js';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql, useFragment, usePreloadedQuery } from '@contra/relay';
import { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { css, styled } from 'styled-components';

const ProfileCompletionChecklist = lazy(async () => {
  return {
    default: (
      await import(
        '#app/components/ProfileCompletionChecklist/ProfileCompletionChecklist.js'
      )
    ).ProfileCompletionChecklist,
  };
});

const PageContainer = styled.div`
  ${({ theme }) => css`
    min-height: 100%;

    background-color: ${theme.colorsV2.white};
    box-shadow: unset;

    & > *:last-child {
      margin-bottom: 124px;
    }
  `}
`;

const ContentContainer = styled(Grid)``;

type ServicePageProps = PageProps<'/s/@slug', Page_SlugSQuery>;

const HybridPage = ({ preloaded }: ServicePageProps) => {
  const data = usePreloadedQuery<Page_SlugSQuery>(
    graphql`
      query Page_SlugSQuery($slug: String!) {
        productizedServiceBySlug(slug: $slug) {
          ...Page_SlugS_productizedService
          ...ProductizedService_productizedService
          ...ProductizedServiceSidebar_productizedService
        }

        visitor {
          id
          userAccount {
            isEligibleForRequestRecommendations
          }
        }
      }
    `,
    preloaded,
  );

  const { productizedServiceBySlug: node, visitor } = data;

  const isMobile = useIsMobile();
  const { hasActivePortfolioSubscription } = useUpgradeToPro();

  const service = useFragment<Page_SlugS_productizedService$key>(
    graphql`
      fragment Page_SlugS_productizedService on ProductizedService {
        id
        publishedAt
        title
        visitorCanEdit
        userProfile {
          id
          firstName
          lastName
          displayUsername
        }
        ...ProductizedService_productizedService
        ...ProductizedServiceSidebar_productizedService
        ...ProductizedServiceAdminActions_productizedService
      }
    `,
    node,
  );

  useRegisterAnalyticEvent(
    service?.id && !service?.visitorCanEdit
      ? {
          eventType: 'ProductizedServiceView',
          input: { productizedServiceId: service.id },
        }
      : undefined,
  );

  const visitorCanEdit = Boolean(service?.visitorCanEdit);
  const hasCompletedProfile = useHasCompletedProfile();
  const showRequestRecommendationCTA = visitorCanEdit && hasCompletedProfile;

  const [urlSearchParameters] = useURLParameters('/s/@slug');
  const justPublished = urlSearchParameters.justPublished;
  const justUpdated = urlSearchParameters.justUpdated;

  const { maybeOpenUserAvailabilityModal } = useUserAvailabilityModal();

  const isDraft = service && !service.publishedAt;
  const isPreview = urlSearchParameters.preview;

  const showPreviewBanner = isDraft && isPreview;

  useMount(() => {
    if (justPublished || justUpdated) {
      // Show availability modal
      maybeOpenUserAvailabilityModal();
    }
  });

  if (!service || !node) return <NotFound />;

  const banner = showPreviewBanner ? (
    <ProductizedServicePreviewBanner />
  ) : (
    <>
      {showRequestRecommendationCTA ? (
        <RequestRecommendationCTA
          isEligibleForRequestRecommendations={
            visitor?.userAccount?.isEligibleForRequestRecommendations ?? false
          }
          page="service"
        >
          Have you completed this service for a client?
        </RequestRecommendationCTA>
      ) : null}
    </>
  );

  return (
    <>
      <Helmet>
        <title>{`${service.title} by ${service.userProfile.firstName} ${service.userProfile.lastName}`}</title>
      </Helmet>

      <MainLayout
        backgroundColor="white"
        banner={banner}
        noMarginOnTheBottom
        username={service.userProfile.displayUsername}
      >
        <ContentContainer>
          <GridColumn
            initial={12}
            lg={8}
          >
            <PageContainer>
              <ProductizedService
                // Key is needed here to enforce re-rendering when changing between services.
                // See: https://www.loom.com/share/6886dee92acf403880d4555f79f00d19
                key={service.id}
                productizedServiceRef={service}
              />
            </PageContainer>
          </GridColumn>
          {!isMobile && (
            <GridColumn initial={4}>
              <ProductizedServiceSidebar
                key={service.id}
                productizedServiceRef={service}
              />
            </GridColumn>
          )}
        </ContentContainer>
        <Suspense>
          <ProfileCompletionChecklist
            variant={hasActivePortfolioSubscription ? 'discover' : 'launch'}
          />
        </Suspense>
        <ProductizedServiceAdminActionsButton productizedServiceRef={service} />
      </MainLayout>
    </>
  );
};

export const Page = ({ preloaded }: ServicePageProps) => {
  const currentUser = useCurrentUser();

  return (
    <SSRSafeRender skipServerRender={Boolean(currentUser)}>
      <HybridPage preloaded={preloaded} />
    </SSRSafeRender>
  );
};
